// SyncLibraryBanner — прогресс первой синхронизации библиотеки (подготовка /
// подключение / загрузка).
// Ошибки sync и offline сюда не попадают: без сети — `OfflineBanner`, а
// временные сбои PowerSync не блокируют offline-работу с уже скачанным курсом.
//
// Баннер обязан быть виден ВЕСЬ первичный синк, а не только пока SDK держит
// соединение: между чекпоинтами `downloading` гаснет, а до первого connect()
// (provider ждёт auth.me) статус вообще молчит — фаза `initializing`. Раньше в
// эти интервалы пользователь смотрел на немой скелетон без единого слова.
//
// Ожидание ПОСЛЕ первичного синка (reopen: `hasSynced` уже true, но данных на
// экране ещё нет) баннер не берёт: это состояние конкретной страницы, а не
// оболочки, и объясняет его гейт самой страницы (`pageWaitLabel`). Иначе
// баннер всплывал бы на каждой перезагрузке любого экрана и дёргал контент
// вниз на свою высоту (`--banners-height`).
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isFirstSyncInProgress, syncStatusLabel } from "@/hooks/sync-status-label";
import { usePowerSyncStatus } from "@/hooks/use-powersync-status";
import { useCurrentUser } from "@/hooks/use-session";
import { SyncProgressStrip } from "./sync-progress-strip";

/** Через сколько ожидания признаём загрузку долгой и объясняем причину. */
const LONG_WAIT_MS = 8_000;

export function SyncLibraryBanner(): React.ReactElement | null {
	const status = usePowerSyncStatus();
	// Гость синк не поднимает вообще — для него `hasSynced` ложен всегда, и без
	// этой проверки фаза `initializing` держала бы баннер на лендинге вечно.
	const isAuthenticated = Boolean(useCurrentUser());
	const { t } = useTranslation("common");
	const visible = isAuthenticated && isFirstSyncInProgress(status);
	const [longWait, setLongWait] = useState(false);

	useEffect(() => {
		if (!visible) {
			setLongWait(false);
			return;
		}
		const id = window.setTimeout(() => setLongWait(true), LONG_WAIT_MS);
		return () => window.clearTimeout(id);
	}, [visible]);

	if (!visible) return null;

	const label = syncStatusLabel(t, status);
	if (!label) return null;

	return (
		<SyncProgressStrip
			label={label}
			phase={status.phase}
			percent={status.downloadProgress?.percent ?? null}
			hint={longWait ? t("offline.syncFirstRunHint") : undefined}
		/>
	);
}
